import { normalizeEmail } from "@event-planner/shared";
import type { Person, Role } from "@event-planner/shared";

type MatchType = "user" | "contact" | "new";

export interface MatchedPerson {
  type: MatchType;
  id: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  role: Role | null;
}

function toMatchedPerson(p: Person, email: string): MatchedPerson {
  return {
    type: p.authUid === null ? "contact" : "user",
    id: p.id,
    email,
    displayName: p.displayName || email,
    photoURL: p.photoURL,
    role: p.role,
  };
}

/**
 * Build the autocomplete option list for the per-event Add Attendee page.
 *
 * Excludes persons already on the event roster, keyed by personId from
 * their `eventParticipants` row (active or pending). That row is the only
 * answer to "is this person on the event" (ADR-193). Invitation records
 * play no part: they are the email delivery log, and a stale one from a
 * deleted person must never hide a re-added person. Revoked rows are not
 * excluded so an organizer can re-add someone after removing them.
 *
 * Under ADR-136 the dual users + contacts merge is gone: one `persons`
 * collection covers both. The `type` field on the returned option is
 * still surfaced for the UX chip (Active vs Contact), derived from
 * `person.authUid` (null implies pre-claim).
 *
 * Pure, exported for unit testing alongside `AttendeeCreatePage`.
 */
export function buildEligibleOptions(
  allPersons: Person[],
  excludedPersonIds: Set<string>,
): MatchedPerson[] {
  const options: MatchedPerson[] = [];
  for (const p of allPersons) {
    const email = p.email;
    if (!email) continue;
    if (excludedPersonIds.has(p.id)) continue;
    options.push(toMatchedPerson(p, email));
  }
  return options;
}

/**
 * Resolve a typed email to a person, roster or not. Email is the first
 * lookup only; everything after uses the person's id.
 */
export function findPersonByEmail(
  allPersons: Person[],
  email: string,
): MatchedPerson | null {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;
  for (const p of allPersons) {
    if (p.email && normalizeEmail(p.email) === normalized) {
      return toMatchedPerson(p, p.email);
    }
  }
  return null;
}
